import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from "@angular/core";
import { UserService } from "../../../domain/user-service";
import {
  AvailabilityZoneSettings,
  ChannelInfo,
  ChannelPlatform,
  EAutoExtendLiveShowBehaviour,
  UploadPlaylistCsvSettings,
  WorkingHoursSettings,
} from "../../../domain/channel";
import { BeforeNextStepEventHandler } from "../../common/wizard/before-next-step.event";
import { ChannelRepository } from "../../../domain/channel-repository";
import { Logger } from "../../../infrastructure/logger";
import { ChannelCodec } from "../../../domain/channel-codec";
import { ActivatedRoute, Params } from "@angular/router";
import { AspectRatio } from "../../../domain/enums";
import { ApplyState } from "../../common/wizard/wizard-nav.component";
import { Category } from "../../../domain/category";
import { CategoryRepository } from "../../../domain/category-repository";
import { ChannelTemplateRepository } from "../../../domain/channel-template-repository";
import { ChannelTemplateRepositoryCached } from "../../../infrastructure/repositories/channel/template/channel-template-repository";
import { DynamicPromoRepository } from "../../../domain/dynamic-promo-repository";
import { TimeSpotRule } from "../../../domain/timespot";
import { Auth, Token } from "../../../infrastructure/auth";
import { UserRepository } from "../../../domain/user-repository";
import { User } from "../../../domain/user";
import { UI } from "../../../services/ui.service";
import { EdgeServer } from "../../../domain/edge-server";
import { ToastrService } from "ngx-toastr";
import { ChannelJsonEditorViewModel } from "../channel-json-editor/channel-json-editor.view-model";
import { NgxSmartModalService } from "ngx-smart-modal";

@Component({
  templateUrl: "./channel-wizard.component.html",
  styleUrls: ["./channel-wizard.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChannelWizardComponent implements OnInit {
  channel: ChannelInfo;
  currentUser: User;
  isCreate: boolean;
  channelCodecs: ChannelCodec[];
  edit = false;

  audioNormalizationChecked: boolean;
  useLufsChecked: boolean;
  audioNormalizationValue: number;
  cuePeriodChecked = false;
  allCategories: Array<Category>;
  allEdgeServers: EdgeServer[];
  relevantEdgeServers: EdgeServer[];

  applyState: ApplyState = ApplyState.Unknown;
  allActiveAvailabilityZones: { id: number; displayName: string }[];
  channelJsonEditorVM: ChannelJsonEditorViewModel;
  allAvailabilityZones: { id: number; displayName: string }[];
  public get channelAvalabilityZones(): { id: number; displayName: string }[] {
    return this.allAvailabilityZones.filter((z) =>
      this.channel.availabilityZoneIds.includes(z.id),
    );
  }

  constructor(
    private userRepository: UserRepository,
    private changeDetectorRef: ChangeDetectorRef,
    private channelRepository: ChannelRepository,
    private logger: Logger,
    private categoryRepository: CategoryRepository,
    private route: ActivatedRoute,
    private auth: Auth,
    private userService: UserService,
    private channelTemplateRepository: ChannelTemplateRepository,
    private dynamicPromoRepository: DynamicPromoRepository,
    private ui: UI,
    private _toastr: ToastrService,
    private _channelRepository: ChannelRepository,
    private _ngxSmartModalService: NgxSmartModalService,
  ) {
    this.allAvailabilityZones =
      userService.getCurrentUser().channelAvailabilityZones;
    // this.allCategories = [new Category(1,'test'),new Category(2,'test'),new Category(3,'test33')]
    categoryRepository.getAllCategoris().then((result) => {
      this.allCategories = result;
      changeDetectorRef.markForCheck();
    });
  }

  ngOnInit() {
    const currentUser = this.userService.getCurrentUser();
    this.allEdgeServers = currentUser.systemEdgeServers;
    this.allActiveAvailabilityZones =
      currentUser.channelAvailabilityZones.filter((z) => z.isActive);

    this.route.params.subscribe((params: Params) => {
      this.channel = undefined;
      this.channelCodecs = undefined;
      if (!!params["id"]) {
        const channelId = parseInt(params["id"]);
        this.isCreate = false;
        this.channelRepository
          .getChannelCodecs(channelId)
          .then((codecs) => {
            this.channelCodecs = codecs;
          })
          .then(() => {
            return this.channelRepository.getChannelInfoById(channelId);
          })
          .then((channel) => {
            this.channel = channel;
            this.allActiveAvailabilityZones =
              currentUser.channelAvailabilityZones.filter(
                (z) =>
                  z.isActive || this.channel.availabilityZoneIds.includes(z.id),
              );
            this.edit = true;
            this.audioNormalizationValue = channel.audioNormalization;
            this.useLufsChecked = channel.useLUFS || false;
            this.audioNormalizationChecked =
              channel.audioNormalization !== null &&
              channel.audioNormalization !== undefined;

            this._bindRelevantEdgeServers();
            this.changeDetectorRef.markForCheck();
          })
          .catch((err) => {
            this.logger.warn("Error getting channel info. Error: " + err);
          });
      } else {
        const defaultTimezoneId = 22;
        const fontSettings = {
          fontId: this.userService.getCurrentUser().fonts[0].id,
          size: 12,
          subtitleStartPositionFromTop: 75,
          subtitleEndPositionFromTop: 90,
        };
        const workingHours: WorkingHoursSettings = new WorkingHoursSettings(
          0,
          0,
          0,
          0,
        ); // default value
        const mainLanguageCode = "en"; // default language

        this.channel = new ChannelInfo(
          undefined,
          "",
          "",
          "",
          undefined,
          undefined,
          undefined,
          undefined,
          defaultTimezoneId,
          true,
          "en",
          fontSettings,
          undefined,
          [],
          AspectRatio.SixteenByNine,
          [],
          [],
          false,
          [],
          "",
          0.9,
          0.1,
          0.5,
          0.5,
          20,
          workingHours,
          mainLanguageCode,
          null,
          false,
          true,
          0,
          false,
          7, // default scheduler days to load       
          false,
          false,
          UploadPlaylistCsvSettings.DEFAULT,
          true,
          null,
        );

        this.currentUser = this.userService.getCurrentUser();
        this.dynamicPromoRepository
          .getTimespotDefaults()
          .then((result) =>
            result.map((rule) => new TimeSpotRule(rule.type, rule.defaultText)),
          )
          .then((defaultTimeSpotRules) => {
            this.channel.timeSpotRules = defaultTimeSpotRules;
          });

        this.channelRepository
          .getChannelCodecs(this.channel.id)
          .then((codecs) => (this.channelCodecs = codecs))
          .catch((err) => [
            this.logger.warn(
              "Could not get channel codecs for channel " + this.channel.id,
            ),
          ]);

        this.isCreate = true;
        this.audioNormalizationChecked = false;
        this.audioNormalizationValue = 0;
      }
    });
  }

  getStepsTitle() {
    return this.isCreate
      ? `Hi ${this.userService.getCurrentUser().username}!
Lets Set Up Your Channel.`
      : `Hello again.
Edit your channel.`;
  }

  getDoneTitle() {
    return `Great Job!
Your channel is good to go.`;
  }

  startCreate(beforeNextStepEvent: BeforeNextStepEventHandler) {
    if (this.channel.id) {
      beforeNextStepEvent.next();
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.channelRepository
      .startCreate(
        this.channel.name,
        this.channel.timezoneId,
        this.channel.logoX,
        this.channel.logoY,
        this.channel.logoHeight,
        this.channel.logoWidth,
        false,
      )
      .then((result) => {
        this.auth.token = new Token(
          result.newAccessToken.token,
          result.newAccessToken.tokenType,
          this.auth.token.expiry,
        );
        this.userRepository.init().then(() => {
          this.currentUser = this.userService.getCurrentUser();
        });

        this.channel.id = result.channelId;
        this.channel.createdDate = result.createdDate;
        beforeNextStepEvent.next();
        this.changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        this.logger.error(
          "channel-wizard.assignChannelId->error in start creating channel. Error " +
            err,
        );
        beforeNextStepEvent.cancel();
      });
  }

  private _validateEdgeServers(): boolean {
    const platformsAvailabilityZoneIds = this.channel.platforms
      .flatMap((p) => p.edgeServerIds)
      .map(
        (edgeServerId) =>
          this.allEdgeServers.find((e) => e.id === edgeServerId)
            .availabilityZoneId,
      );
    for (let i = 0; i < this.channel.availabilityZoneIds.length; i++) {
      const chosenAvailabilityZone = this.allAvailabilityZones.find(
        (z) => z.id === this.channel.availabilityZoneIds[i],
      );
      if (
        platformsAvailabilityZoneIds.includes(chosenAvailabilityZone.id) ===
        false
      ) {
        this._toastr.error(
          `ERROR! Availability Zone ${chosenAvailabilityZone.displayName} is not associated with any platform.`,
        );
        return false;
      }
    }

    for (let i = 0; i < this.channel.platforms.length; i++) {
      const platform = this.channel.platforms[i];
      if (platform.isActive) {
        for (let j = 0; j < platform.edgeServerIds.length; j++) {
          const edgeServerId = platform.edgeServerIds[j];
          const relevantEdgeServer = this.allEdgeServers.find(
            (e) => e.id === edgeServerId,
          );
          if (
            this.channel.availabilityZoneIds.includes(
              relevantEdgeServer.availabilityZoneId,
            ) === false
          ) {
            this._toastr.error(
              `ERROR! Platform ${platform.displayLabel} has edge server ${relevantEdgeServer.name} selected, which is not associated with any chosen availability zone.`,
            );
            return false;
          }
        }
      }
    }

    //validate hls and webrtc edge servers
    const chosenActivePlatforms = this.channel.platforms.filter(
      (p) => p.isActive,
    );
    for (let i = 0; i < chosenActivePlatforms.length; i++) {
      const currentPlatform = chosenActivePlatforms[i];
      if (
        !currentPlatform.edgeServerIds ||
        currentPlatform.edgeServerIds.length === 0
      ) {
        this._toastr.error(
          `ERROR! Platform ${currentPlatform.displayLabel} doesn't have any edge server selected.`,
        );
        return false;
      }
    }
    const platformEdgeServerIds = chosenActivePlatforms.flatMap(
      (p) => (p as ChannelPlatform).edgeServerIds,
    );
    if (!this.channel.webRtcPreviewEdgeServerId) {
      this._toastr.error(`ERROR! WebRTC Edge Server is not selected!`);
      return false;
    }
    if (!this.channel.hlsPreviewEdgeServerId) {
      this._toastr.error(`ERROR! HLS Edge Server is not selected!`);
      return false;
    }
    if (!this.channel.futurePreviewEdgeServerId) {
      this._toastr.error(`ERROR! Future Preview Edge Server is not selected!`);
      return false;
    }
    const webRtcEdgeServer = this.allEdgeServers.find(
      (e) => e.id === this.channel.webRtcPreviewEdgeServerId,
    );
    const hlsEdgeServer = this.allEdgeServers.find(
      (e) => e.id === this.channel.hlsPreviewEdgeServerId,
    );
    if (
      this.channel.availabilityZoneIds.includes(
        webRtcEdgeServer.availabilityZoneId,
      ) === false
    ) {
      this._toastr.error(
        `ERROR! WebRTC Edge Server ${webRtcEdgeServer.name} is not associated with any chosen availability zone.`,
      );
      return false;
    }
    if (platformEdgeServerIds.includes(webRtcEdgeServer.id) === false) {
      this._toastr.error(
        `ERROR! WebRTC Edge Server ${webRtcEdgeServer.name} is not associated with any platform.`,
      );
      return false;
    }

    if (
      this.channel.availabilityZoneIds.includes(
        hlsEdgeServer.availabilityZoneId,
      ) === false
    ) {
      this._toastr.error(
        `ERROR! HLS Edge Server ${hlsEdgeServer.name} is not associated with any chosen availability zone.`,
      );
      return false;
    }
    if (platformEdgeServerIds.includes(hlsEdgeServer.id) === false) {
      this._toastr.error(
        `ERROR! HLS Edge Server ${hlsEdgeServer.name} is not associated with any platform.`,
      );
      return false;
    }
    return true;
  }
  onApply() {
    if (
      this.channel.logoX === null ||
      this.channel.logoY === null ||
      this.channel.logoWidth === null ||
      this.channel.logoHeight === null
    ) {
      this.logger.error(
        "channel-wizard.onFinish->error saving. Error logo values are invalid",
      );
      this.activateApplyNotification(ApplyState.Failed);
      return;
    }

    if (this._validateEdgeServers() === false) {
      return;
    }
    this.channel.audioNormalization = this.audioNormalizationChecked
      ? this.audioNormalizationValue
      : null;

    this.channel.useLUFS = this.audioNormalizationChecked
      ? this.useLufsChecked
      : null;
    console.log("onApply()", this.channel);

    let availabilityZonesOptions = null;
    let alertMsg = " ";

    if (this.channel.isActive) {
      availabilityZonesOptions = this.userService
        .getCurrentUser()
        .channelAvailabilityZones.filter((z) => z.isActive)
        .map((zone) => {
          return {
            id: zone.id,
            displayName: zone.displayName,
            selected: false,
          };
        });
      alertMsg =
        '<p style="text-align: left">The channel is currently active. <br>Choose where you want to deploy the new settings to: </p>';
    }

    this.ui.notify(this.ui.EVENTS.ShowConfirm, {
      title: "Are you sure?",
      message: alertMsg,
      options: availabilityZonesOptions,
      buttons: [
        {
          title: "NO",
          callback: () => {
            // go to channels page
          },
        },
        {
          title: "YES",
          callback: async () => {
            try {
              console.log("onApply", availabilityZonesOptions);

              let selectedAvailabilityZonesToDeployOptions = null;

              if (availabilityZonesOptions) {
                selectedAvailabilityZonesToDeployOptions =
                  availabilityZonesOptions
                    .filter((o) => o.selected)
                    .map((o) => o.id);
              }
              await this.channelRepository.save(
                this.channel,
                selectedAvailabilityZonesToDeployOptions,
                this.cuePeriodChecked,
              );
              this.channel = await this.channelRepository.getChannelInfoById(
                this.channel.id,
              );

              this.activateApplyNotification(ApplyState.Succeeded);

              if (this.userService.getCurrentUser().isSuperUser) {
                this.refreshChannelTemplateCache();
              }
            } catch (err) {
              this.logger.error(
                "channel-wizard.onFinish->error saving. Error " + err,
              );
              this.activateApplyNotification(ApplyState.Failed);
            }
          },
        },
      ],
    });
  }

  activateApplyNotification(state: ApplyState) {
    this.applyState = state;
    this.changeDetectorRef.markForCheck();

    setTimeout(() => {
      this.applyState = ApplyState.Unknown;
      this.changeDetectorRef.markForCheck();
    }, 2500);
  }

  onFinish(beforeNextStepEvent: BeforeNextStepEventHandler) {
    this.channel.audioNormalization = this.audioNormalizationChecked
      ? this.audioNormalizationValue
      : 0;
    this.channelRepository
      .save(this.channel, null, this.cuePeriodChecked)
      .then(async () => {
        this.channel = await this.channelRepository.getChannelInfoById(
          this.channel.id,
        );
        /*if (this.userService.getCurrentUser().isSuperUser) {
        this.refreshChannelTemplateCache();
      }*/

        beforeNextStepEvent.next();
        this.changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        this.logger.error(
          "channel-wizard.onFinish->error saving. Error " + err,
        );
        beforeNextStepEvent.cancel();
      });
  }
  _bindRelevantEdgeServers() {
    this.relevantEdgeServers = this.allEdgeServers.filter((e) =>
      this.channel.availabilityZoneIds.includes(e.availabilityZoneId),
    );
  }

  saveCodec(codec) {
    if (!codec.id) {
      this.addNewCodec(codec);
    } else {
      this.updateCodec(codec);
    }
  }

  addNewCodec(codec) {
    this.channelRepository
      .addChannelCodec(codec, this.channel.id)
      .then((codecId) => {
        codec.id = codecId;
        this.channelCodecs.push(codec);
      })
      .catch((err) => {
        this.logger.error("Failed to add new codec");
      });
  }

  updateCodec(codec) {
    this.channelRepository
      .updateChannelCodec(codec, this.channel.id)
      .then(() => {
        const index = this.channelCodecs.findIndex((x) => x.id === codec.id);
        if (index > -1) {
          this.channelCodecs.splice(index, 1, codec);
        } else {
          this.channelCodecs.push(codec);
        }
        this.changeDetectorRef.markForCheck();
      })
      .catch((err) => {
        this.logger.error("Failed to update the codec");
      });
  }

  refreshChannelTemplateCache() {
    (this.channelTemplateRepository as ChannelTemplateRepositoryCached).init();
  }
  overrideChannelSettingsClicked(event) {
    const chosenAvailabilityZoneId = event.item.value;
    const chosenAvailabilityZoneSettings = this.channel.availabilityZones.find(
      (x) => x.id === chosenAvailabilityZoneId,
    );
    const availabilityZoneName = this.allAvailabilityZones.find(
      (x) => x.id === chosenAvailabilityZoneId,
    ).displayName;
    this.channelJsonEditorVM = new ChannelJsonEditorViewModel(
      chosenAvailabilityZoneSettings,
      availabilityZoneName,
      this.channel,
      () => {
        try {
          return this._channelRepository
            .generateOverrideChannelJSON(
              this.channel.id,
              chosenAvailabilityZoneId,
            )
            .then((result) => {
              this._toastr.success(
                `Successfully generated a new channel json.`,
              );

              this.changeDetectorRef.markForCheck();
              this.changeDetectorRef.detectChanges();
              return result;
            });
        } catch (e) {
          this._toastr.error(
            `Failed to add the channel override. Please check connection to the controller ${availabilityZoneName}`,
          );
          return Promise.resolve(
            chosenAvailabilityZoneSettings.overrideChannelJson,
          );
        }
      },
      () => {
        this._ngxSmartModalService.getModal("channelJsonEditorModal").close();
      },
      () => {
        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
      },
      this._toastr,
    );

    this._ngxSmartModalService.getModal("channelJsonEditorModal").open();
    console.log(
      "subste1->overrideChannelSettingsClicked",
      chosenAvailabilityZoneSettings,
    );
  }
  async onOverrideChannelJsonZoneChanged(chosenAvailabilityZoneIds: number[]) {
    const updatedChannelAvailabilityZones = [];
    for (let i = 0; i < this.channel.availabilityZones.length; i++) {
      const currentAvailabilityZoneSettings = this.channel.availabilityZones[i];

      if (
        chosenAvailabilityZoneIds.includes(currentAvailabilityZoneSettings.id)
      ) {
        let overrideChannelJson = null;
        const existingAvailabilityZone = this.channel.availabilityZones.find(
          (az) => currentAvailabilityZoneSettings.id === az.id,
        );
        if (existingAvailabilityZone.overrideChannelJson) {
          overrideChannelJson = existingAvailabilityZone.overrideChannelJson;
        } else if (
          chosenAvailabilityZoneIds.includes(existingAvailabilityZone.id)
        ) {
          try {
            overrideChannelJson =
              await this._channelRepository.generateOverrideChannelJSON(
                this.channel.id,
                existingAvailabilityZone.id,
              );
          } catch (e) {
            this._toastr.error(
              `Failed to add the channel override. Please check connection to the controller ${this.allAvailabilityZones.find((x) => x.id === existingAvailabilityZone.id).displayName}`,
            );
          }
        }
        updatedChannelAvailabilityZones.push(
          new AvailabilityZoneSettings(
            currentAvailabilityZoneSettings.id,
            overrideChannelJson,
          ),
        );
      } else {
        currentAvailabilityZoneSettings.overrideChannelJson = null;
        updatedChannelAvailabilityZones.push(currentAvailabilityZoneSettings);
      }
    }
    this.channel.availabilityZones = updatedChannelAvailabilityZones;
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }
  updateAutoExtendLiveShowBehaviour(value: number) {
    this.channel.autoExtendLiveShowBehaviour =
      value as EAutoExtendLiveShowBehaviour;
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }
}
